import { QueryClient } from '@tanstack/react-query';

/**
 * Query key prefixes for tiered caching strategy
 */
export const QueryKeyPrefixes = {
  STATIC: 'static',      // Crops, growth stages - never refetch
  USER: 'user',          // Profile, settings - 24h
  ACTIVITIES: 'activities', // Activities, photos - 1h
  SCORE: 'score',        // Score data - 30min
  FEED: 'feed',          // Network feed - 5min
} as const;

/**
 * Stale time configurations in milliseconds
 */
export const StaleTimes = {
  STATIC: Infinity,                    // Never refetch static data
  USER: 1000 * 60 * 60 * 24,          // 24 hours
  ACTIVITIES: 1000 * 60 * 60,         // 1 hour
  SCORE: 1000 * 60 * 30,              // 30 minutes
  FEED: 1000 * 60 * 5,                // 5 minutes
  DEFAULT: 1000 * 60 * 5,             // 5 minutes fallback
} as const;

/**
 * GC time - 7 days for offline access
 */
export const GC_TIME = 1000 * 60 * 60 * 24 * 7;

/**
 * Get stale time based on query key prefix
 */
export const getStaleTimeForKey = (queryKey: readonly unknown[]): number => {
  const firstKey = queryKey[0];
  
  if (typeof firstKey !== 'string') {
    return StaleTimes.DEFAULT;
  }
  
  // Check for prefix matches
  if (firstKey === QueryKeyPrefixes.STATIC || firstKey === 'crops' || firstKey === 'growthStages') {
    return StaleTimes.STATIC;
  }
  
  if (firstKey === QueryKeyPrefixes.USER || firstKey === 'profile' || firstKey === 'settings') {
    return StaleTimes.USER;
  }
  
  if (firstKey === QueryKeyPrefixes.ACTIVITIES || firstKey === 'activities' || firstKey === 'photos') {
    return StaleTimes.ACTIVITIES;
  }
  
  if (firstKey === QueryKeyPrefixes.SCORE || firstKey === 'score' || firstKey === 'leaderboard') {
    return StaleTimes.SCORE;
  }
  
  if (firstKey === QueryKeyPrefixes.FEED || firstKey === 'network' || firstKey === 'alerts') {
    return StaleTimes.FEED;
  }
  
  return StaleTimes.DEFAULT;
};

// FIX-CCS-OUTBOX (spec §7.6): the unused idbPersister (key agxl-query-cache, no caller) was removed; the live
// query cache persistence is src/lib/queryPersistence.ts (key agxl-react-query-cache), mounted in main.tsx.

/**
 * Create and configure the query client with offline-first defaults
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Offline-first: use cache first, then fetch in background
      networkMode: 'offlineFirst',
      
      // Default stale time (overridden per-query based on key)
      staleTime: StaleTimes.DEFAULT,
      
      // 7 days GC time for offline access
      gcTime: GC_TIME,
      
      // Retry configuration for unreliable networks
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Don't refetch aggressively - save data
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: 'always', // Do refetch when coming back online
      
      // Keep previous data while fetching for smoother UX
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      // Offline-first for mutations too
      networkMode: 'offlineFirst',
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/**
 * FIX-SEASON-GATE: reads that decide what the farmer is ALLOWED to do (start a season, pass the
 * consent gate) — not just what they see. They are never persisted to IndexedDB, never restored
 * from it, never shown from another key's data, and always re-read when a screen that uses them
 * opens. A stale or error-shaped answer must never decide an entitlement.
 * FIX-SEASON-GATE-2 (Adeel D1): five keys. consent-records, aak-consent and loan-eligibility are
 * display data and stay persisted (the Offline programme promises last-saved data).
 */
export const ENTITLEMENT_QUERY_KEYS = [
  'my-open-seasons',
  'season-catalogue-published',
  'farmer-active-cycle',
  'active-cycle-guard',
  'reconsent-status',
] as const;

const ENTITLEMENT_KEY_SET: ReadonlySet<unknown> = new Set(ENTITLEMENT_QUERY_KEYS);

export const isEntitlementQueryKey = (queryKey: readonly unknown[]): boolean => ENTITLEMENT_KEY_SET.has(queryKey[0]);

// FIX-PRELOGIN-CACHE: queries that must never be written to (or restored from) the IndexedDB cache — the pre-login
// video list changes with every publish; a persisted copy showed farmers an old list for up to 7 days.
export const NON_PERSISTED_QUERY_KEYS = ['prelogin-videos'] as const;
const NON_PERSISTED_KEY_SET: ReadonlySet<unknown> = new Set(NON_PERSISTED_QUERY_KEYS);
export const isNonPersistedQueryKey = (queryKey: readonly unknown[]): boolean =>
  isEntitlementQueryKey(queryKey) || NON_PERSISTED_KEY_SET.has(queryKey[0]);

ENTITLEMENT_QUERY_KEYS.forEach((key) => {
  queryClient.setQueryDefaults([key], { refetchOnMount: 'always', placeholderData: undefined });
});

/**
 * Serialize query cache for persistence
 */
export const serializeQueryCache = () => {
  const cache = queryClient.getQueryCache();
  const queries = cache.getAll();

  return queries
    .filter(query => {
      // Only persist successful queries with data — never an entitlement read (FIX-SEASON-GATE)
      // ... nor a pre-login video list (FIX-PRELOGIN-CACHE)
      return query.state.status === 'success' && query.state.data !== undefined && !isNonPersistedQueryKey(query.queryKey);
    })
    .map(query => ({
      queryKey: query.queryKey,
      queryHash: query.queryHash,
      state: query.state,
    }));
};

/**
 * Restore query cache from persistence
 */
export const restoreQueryCache = (persistedData: ReturnType<typeof serializeQueryCache>) => {
  if (!Array.isArray(persistedData)) return;

  persistedData.forEach(({ queryKey, state }) => {
    // A cache written by an older build may still hold entitlement reads — drop them (FIX-SEASON-GATE).
    // ... and an old pre-login video list (FIX-PRELOGIN-CACHE).
    if (isNonPersistedQueryKey(queryKey)) return;
    queryClient.setQueryData(queryKey, state.data, {
      updatedAt: state.dataUpdatedAt,
    });
  });
};
